<template>
  <AppLayout>
    <template #header>
      <PageHeadingWithActions
        :title="getMunicipalitiesFromTripLegs(props.trip.legs)"
        :metas="metas"
      >
        <template #actions v-if="useUser">
          <ButtonBase v-if="canEdit" color="red" @click="confirmDelete"
            >Delete</ButtonBase
          >

          <ButtonBase
            v-if="canEdit"
            class="ml-2"
            color="secondary"
            :href="route('trips.edit', props.trip)"
            >Edit</ButtonBase
          >
          <ButtonBase
            external
            class="ml-2"
            color="white"
            size="xs"
            :href="route('trips.calendar', props.trip)"
            ><HeroiconContainer :icon="CalendarIcon"
          /></ButtonBase>
        </template>
      </PageHeadingWithActions>
    </template>

    <div class="grid grid-cols-2 gap-4">
      <div class="col-span-2">
        <StackedListContainer>
          <StackedListRow
            v-for="leg in props.trip.legs"
            :key="leg.id"
            :href="route('trips.legs.show', { trip: trip, leg: leg })"
          >
            <LegStackedListRow :leg="leg" :showCrewWarnings="canManagePilots" />
          </StackedListRow>
        </StackedListContainer>
      </div>

      <div class="col-span-2 sm:col-span-1 md:col-span-2 lg:col-span-1">
        <TableBase>
          <template #before>
            <CardHeadingWithDescriptionAction title="Passengers">
              <template #actions>
                <ButtonCircularPlus
                  v-if="canEdit"
                  @click="addFlyer"
                  size="xs"
                />
              </template>
            </CardHeadingWithDescriptionAction>
          </template>
          <template #tbody>
            <tr v-for="flyer in props.trip.flyers" :key="flyer.id">
              <TableCellBase bold>{{ flyer.name }}</TableCellBase>
              <TableCellBase right
                ><LinkBase v-if="canEdit" @click="removeFlyer(flyer)"
                  >Remove</LinkBase
                ></TableCellBase
              >
            </tr>
          </template>
        </TableBase>
      </div>

      <CardBase class="col-span-2">
        <CardHeadingWithDescriptionAction title="Crew">
          <template #actions>
            <ButtonCircularPlus
              v-if="canManagePilots"
              @click="addPilot"
              size="xs"
            />
          </template>
        </CardHeadingWithDescriptionAction>

        <CrewGrid
          :trip="props.trip"
          :legs="props.trip.legs"
          :planePilots="props.planePilots"
          :canManagePilots="canManagePilots"
          @assign="assignSlot"
          @remove="removePilot"
          @copy="confirmCopy"
        />
      </CardBase>

      <div
        v-if="props.canManage"
        class="col-span-2 sm:col-span-1 md:col-span-2 lg:col-span-1"
      >
        <TableBase>
          <template #before>
            <CardHeadingWithDescriptionAction title="Owners">
              <template #actions>
                <ButtonCircularPlus
                  v-if="canManage"
                  @click="addOwner"
                  size="xs"
                />
              </template>
            </CardHeadingWithDescriptionAction>
          </template>
          <template #tbody>
            <tr v-for="owner in trip.owners" :key="owner.id">
              <TableCellBase bold>{{ owner.name }}</TableCellBase>
              <TableCellBase right
                ><LinkBase v-if="canManage" @click="removeOwner(owner)"
                  >Remove</LinkBase
                ></TableCellBase
              >
            </tr>
          </template>
        </TableBase>
      </div>

      <CardBase class="col-span-2 sm:col-span-1 md:col-span-2 lg:col-span-1">
        <CardHeadingWithDescriptionAction title="Comments" />

        <CardPadding>
          <TripActivityFeed :trip="props.trip" :canComment="props.canComment" />
        </CardPadding>
      </CardBase>
    </div>

    <ModalSimpleAlertWithDismiss
      @confirm="deleteFlyer"
      @close="removeFlyerModalOpen = false"
      :show="removeFlyerModalOpen"
      title="Remove passenger"
      description="Are you sure you want to remove this person from the trip?"
    />

    <ModalSimpleAlertWithDismiss
      @confirm="deletePilot"
      @close="removePilotModalOpen = false"
      :show="removePilotModalOpen"
      title="Remove pilot"
      description="Are you sure you want to remove this pilot from the trip?"
    />

    <ModalSimpleAlertWithDismiss
      @confirm="deleteOwner"
      @close="removeOwnerModalOpen = false"
      :show="removeOwnerModalOpen"
      title="Remove owner"
      description="Are you sure you want to remove this owner from the trip? They may remain a flyer but will not be accounted for during invoicing."
    />

    <ModalSimpleAlertWithDismiss
      @confirm="deleteTrip"
      @close="deleteModalOpen = false"
      :show="deleteModalOpen"
      title="Delete trip"
      description="Are you sure you want to delete this trip? The trip will be removed from the calendar."
    />

    <AddFlyer
      :show="addFlyerModalOpen"
      @close="addFlyerModalOpen = false"
      :currentFlyers="props.trip.flyers"
      :trip="props.trip"
    />

    <AddPilot
      :show="addPilotModalOpen"
      @close="addPilotModalOpen = false"
      :currentPilots="props.trip.pilots"
      :legs="props.trip.legs"
      :trip="props.trip"
      :presetLeg="assigning?.leg"
      :presetRole="assigning?.role"
    />

    <ModalSimpleAlertWithDismiss
      @confirm="copyCrew"
      @close="copyingFrom = null"
      :show="!!copyingFrom"
      title="Use this crew for every leg"
      :description="copyDescription"
      confirmLabel="Use for all legs"
    />

    <AddOwner
      :show="addOwnerModalOpen"
      @close="addOwnerModalOpen = false"
      :currentOwners="props.trip.owners"
      :trip="props.trip"
    />
  </AppLayout>
</template>

<script setup>
import {
  getAirportCodesFromTripLegs,
  getMunicipalitiesFromTripLegs,
} from './Common'

import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import ButtonCircularPlus from '@/Common/Elements/Buttons/ButtonCircularPlus.vue'
import HeroiconContainer from '@/Common/Elements/HeroiconContainer.vue'
import CardHeadingWithDescriptionAction from '@/Common/Headings/CardHeadings/CardHeadingWithDescriptionAction.vue'
import PageHeadingWithActions from '@/Common/Headings/PageHeadings/PageHeadingWithActions.vue'
import CardBase from '@/Common/Layout/Containers/CardBase.vue'
import CardPadding from '@/Common/Layout/Containers/CardPadding.vue'
import StackedListContainer from '@/Common/Lists/StackedLists/StackedListContainer.vue'
import StackedListRow from '@/Common/Lists/StackedLists/StackedListRow.vue'
import TableBase from '@/Common/Lists/Tables/TableBase.vue'
import TableCellBase from '@/Common/Lists/Tables/TableCellBase.vue'
import ModalSimpleAlertWithDismiss from '@/Common/Overlays/Modals/ModalSimpleAlertWithDismiss.vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { useUser } from '@/Util/UseUser'
import {
  CalendarIcon,
  HashtagIcon,
  LocationMarkerIcon,
} from '@heroicons/vue/solid'
import { Inertia } from '@inertiajs/inertia'
import { computed, ref } from 'vue'
import AddFlyer from './AddFlyer.vue'
import AddOwner from './AddOwner.vue'
import AddPilot from './AddPilot.vue'
import CrewGrid from './CrewGrid.vue'
import LegStackedListRow from './LegStackedListRow.vue'
import TripActivityFeed from './TripActivityFeed.vue'
import { legRoute, legShortDate } from '../Calendar/pilotColors'

const props = defineProps({
  trip: Object,
  canEdit: Boolean,
  canManage: Boolean,
  canManagePilots: Boolean,
  canComment: Boolean,
  planePilots: Array,
})

const metas = computed(() => {
  return [
    { text: props.trip.plane.tail_number, icon: HashtagIcon },
    {
      text: getAirportCodesFromTripLegs(props.trip.legs)?.join(', '),
      icon: LocationMarkerIcon,
    },
  ]
})

// Flyers
const removeFlyerModalOpen = ref(false)
const removePilotModalOpen = ref(false)
const removeOwnerModalOpen = ref(false)
const flyerRemoving = ref(null)
const pilotRemoving = ref(null)
const ownerRemoving = ref(null)
const addFlyerModalOpen = ref(false)
const addPilotModalOpen = ref(false)
const addOwnerModalOpen = ref(false)

function removeFlyer(flyer) {
  flyerRemoving.value = flyer
  removeFlyerModalOpen.value = true
}

function deleteFlyer() {
  Inertia.put(
    route('trips.remove-flyer', props.trip),
    {
      user: flyerRemoving.value,
    },
    {
      onFinish: () => (removeFlyerModalOpen.value = false),
    }
  )
}

function removePilot(pilot) {
  pilotRemoving.value = pilot
  removePilotModalOpen.value = true
}

// Crew slots
const assigning = ref(null) // { leg, role } while filling one slot from the grid

function assignSlot(leg, role) {
  assigning.value = { leg, role }
  addPilotModalOpen.value = true
}

const copyingFrom = ref(null)

function confirmCopy(leg) {
  copyingFrom.value = leg
}

const copyDescription = computed(() =>
  copyingFrom.value
    ? `Every other leg will get the same PIC and SIC as ${legRoute(
        copyingFrom.value
      )} (${legShortDate(
        copyingFrom.value
      )}). Pilots who were only on other legs are taken off the trip.`
    : ''
)

function copyCrew() {
  Inertia.post(
    route('trips.legs.crew.copy', {
      trip: props.trip.id,
      leg: copyingFrom.value.id,
    }),
    {},
    {
      preserveScroll: true,
      onFinish: () => (copyingFrom.value = null),
    }
  )
}

function deletePilot() {
  Inertia.put(
    route('trips.remove-pilot', props.trip),
    {
      user: pilotRemoving.value,
    },
    {
      onFinish: () => (removePilotModalOpen.value = false),
    }
  )
}

function removeOwner(owner) {
  ownerRemoving.value = owner
  removeOwnerModalOpen.value = true
}

function deleteOwner() {
  Inertia.put(
    route('trips.remove-owner', props.trip),
    {
      user: ownerRemoving.value,
    },
    {
      onFinish: () => (removeOwnerModalOpen.value = false),
    }
  )
}

function addFlyer() {
  addFlyerModalOpen.value = true
}

function addPilot() {
  assigning.value = null
  addPilotModalOpen.value = true
}

function addOwner() {
  addOwnerModalOpen.value = true
}

// Trip delete
const deleteModalOpen = ref(false)

function confirmDelete() {
  deleteModalOpen.value = true
}

function deleteTrip() {
  deleteModalOpen.value = false
  Inertia.delete(route('trips.destroy', props.trip))
}
</script>
